import React, { useMemo, useState } from "react";
import {
  ChevronDownRegular,
  ChevronRightRegular,
  ShieldCheckmarkFilled,
} from "@fluentui/react-icons";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import supersub from "remark-supersub";
import type { ConsoleCitation, ConsoleSource } from "../../api/types";
import { citationsFromSources } from "../../api/conversationIdentity";
import { parseAnswer } from "./AnswerParser";
import styles from "./Answer.module.css";

export type AnswerProps = {
  answer: string;
  sources?: ConsoleSource[];
  citations?: ConsoleCitation[];
  onCitationClicked?: (citation: ConsoleCitation) => void;
};

function reviewedDay(value: string): string {
  const day = value.slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(day) ? day : value;
}

function isWebCitation(citation: ConsoleCitation): boolean {
  const url = citation.url || "";
  return /^https?:\/\//i.test(url) && !citation.document_id;
}

function citationLabel(citation: ConsoleCitation, index: number): string {
  const raw = citation.title || citation.filepath || citation.path;
  if (!raw) {
    return `Citation ${index}`;
  }
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
}

export function Answer({
  answer,
  sources = [],
  citations,
  onCitationClicked,
}: AnswerProps) {
  const parsedAnswer = useMemo(() => {
    const resolved =
      citations && citations.length > 0 ? citations : citationsFromSources(sources);
    return parseAnswer({ answer, citations: resolved });
  }, [answer, citations, sources]);
  const [chevronIsExpanded, setChevronIsExpanded] = useState(false);
  const referencesFooterLabel =
    parsedAnswer.citations.length === 1
      ? "1 reference"
      : `${parsedAnswer.citations.length} references`;

  const openCitation = (citation: ConsoleCitation) => {
    if (onCitationClicked) {
      onCitationClicked(citation);
      return;
    }
    setChevronIsExpanded(true);
  };

  return (
    <div className={styles.answerContainer} data-testid="console-answer-body">
      <ReactMarkdown
        remarkPlugins={[remarkGfm, supersub]}
        className={styles.answerText}
        components={{
          p: ({ children }) => (
            <p style={{ marginBottom: "16px", marginTop: "0" }}>{children}</p>
          ),
          a: ({ href, children, ...rest }) => {
            const target = typeof href === "string" ? href : "";
            const external = /^https?:\/\//i.test(target);
            return (
              <a
                href={target}
                {...rest}
                {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
              >
                {children}
              </a>
            );
          },
          strong: ({ children }) => <strong>{children}</strong>,
          sup: ({ children }) => {
            const text = String(
              React.Children.toArray(children).reduce(
                (acc, child) =>
                  acc +
                  (typeof child === "string" || typeof child === "number"
                    ? String(child)
                    : ""),
                "",
              ),
            ).trim();
            const n = parseInt(text, 10);
            if (!Number.isNaN(n) && n >= 1 && n <= parsedAnswer.citations.length) {
              const citation = parsedAnswer.citations[n - 1] as ConsoleCitation;
              return (
                <sup
                  className={styles.clickableSup}
                  role="button"
                  tabIndex={0}
                  title={citationLabel(citation, n)}
                  data-testid="console-citation-sup"
                  onClick={() => openCitation(citation)}
                  onKeyDown={(event) => {
                    if (event.key === " " || event.key === "Enter") {
                      event.preventDefault();
                      openCitation(citation);
                    }
                  }}
                >
                  {children}
                </sup>
              );
            }
            return <sup>{children}</sup>;
          },
        }}
      >
        {parsedAnswer.markdownFormatText}
      </ReactMarkdown>
      {parsedAnswer.citations.length > 0 ? (
        <div className={styles.answerFooter}>
          <div className={styles.referencesFooterRow} aria-label="References">
            <ShieldCheckmarkFilled
              className={styles.responseFlowTrustShield}
              aria-hidden
              data-testid="console-trust-shield"
            />
            <div
              className={styles.referencesToggle}
              tabIndex={0}
              role="button"
              onClick={() => setChevronIsExpanded((open) => !open)}
              onKeyDown={(event) => {
                if (event.key === " " || event.key === "Enter") {
                  event.preventDefault();
                  setChevronIsExpanded((open) => !open);
                }
              }}
              data-testid="toggle-citations-list"
            >
              <span className={styles.accordionTitle}>
                <span data-testid="no-of-references">{referencesFooterLabel}</span>
              </span>
              {chevronIsExpanded ? (
                <ChevronDownRegular
                  className={styles.accordionIcon}
                  data-testid="chevron-icon"
                />
              ) : (
                <ChevronRightRegular
                  className={styles.accordionIcon}
                  data-testid="chevron-icon"
                />
              )}
            </div>
          </div>
          {chevronIsExpanded ? (
            <div data-testid="citations-container">
              {parsedAnswer.citations.map((citation, index) => {
                const displayIdx = index + 1;
                const label = citationLabel(citation, displayIdx);
                const reviewed = citation.last_reviewed
                  ? reviewedDay(String(citation.last_reviewed))
                  : "";
                const marking = (citation.marking || "").trim();
                const hostLine = isWebCitation(citation)
                  ? (() => {
                      try {
                        return new URL(String(citation.url)).hostname;
                      } catch {
                        return null;
                      }
                    })()
                  : null;
                return (
                  <span
                    data-testid="console-citation"
                    key={`${citation.document_id || citation.id || label}-${index}`}
                    className={
                      isWebCitation(citation)
                        ? styles.citationWebRow
                        : styles.citationContainer
                    }
                    title={label}
                    role="button"
                    tabIndex={0}
                    onClick={() => openCitation(citation)}
                    onKeyDown={(event) => {
                      if (event.key === " " || event.key === "Enter") {
                        event.preventDefault();
                        openCitation(citation);
                      }
                    }}
                  >
                    <div className={styles.citation}>{displayIdx}</div>
                    <span className={styles.consoleSourcePath}>{label}</span>
                    {hostLine ? (
                      <span className={styles.citationWebHostLine}>{hostLine}</span>
                    ) : null}
                    <span className={styles.consoleSourceMeta}>
                      {marking ? <span>{marking}</span> : null}
                      {marking && reviewed ? " · " : null}
                      {reviewed ? (
                        <span data-testid="console-citation-last-reviewed">
                          {reviewed}
                        </span>
                      ) : null}
                    </span>
                  </span>
                );
              })}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
